/**
 * YjsWebPubSubProvider - Bridges Yjs CRDT sync over Azure Web PubSub.
 *
 * Uses the PubSubCollaborationService singleton (collaboration hub) to relay
 * Yjs document updates and awareness state between peers.
 *
 * Architecture:
 *   Y.Doc ←→ YjsWebPubSubProvider ←→ PubSubCollaborationService ←→ WebPubSub ←→ peers
 *
 * The provider:
 *   1. Listens for local Y.Doc updates and broadcasts them as base64 doc_op messages
 *   2. Receives remote doc_op messages and applies them to the local Y.Doc
 *   3. Manages Awareness (cursor positions, user presence) via the same channel
 *   4. Handles sync protocol (step1/step2) for initial state exchange
 *
 * IMPORTANT: Uses deterministic clientID (INITIAL_CONTENT_CLIENT_ID = 1) for
 * initial HTML loading to prevent content duplication when Yjs merges.
 */

import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness';
import { getCollaborationService } from './PubSubCollaborationService';

// Deterministic client ID used when loading initial HTML content.
// This ensures all peers' initial-load operations are de-duplicated by Yjs
// (same clientID + same clock = same operation = merged, not appended).
const INITIAL_CONTENT_CLIENT_ID = 1;

// How often to re-broadcast awareness (cursor + user info) to peers.
// This ensures peers eventually get our state even if the first message was lost.
const AWARENESS_RESEND_INTERVAL = 15_000; // 15s

// Delay after joining group before sending initial sync/awareness.
// Gives WebPubSub time to propagate group membership.
const POST_JOIN_DELAY = 300; // 300ms

const LOG_PREFIX = '[YjsProvider]';

export interface YjsUser {
  id: string;
  name: string;
  color: string;
  email?: string;
  profilePicture?: string;
}

export interface YjsWebPubSubProviderOptions {
  /** Y.Doc instance to sync */
  doc: Y.Doc;
  /** Matter UUID - used as the PubSub group */
  matterId: string;
  /** Document ID (draft/insight type) - scopes Yjs messages */
  documentId: string;
  /** Current user info for awareness */
  user: YjsUser;
  /** Optional pre-existing Awareness instance (shared with CollaborationCursor) */
  awareness?: awarenessProtocol.Awareness;
}

type DestroyCallback = () => void;

export class YjsWebPubSubProvider {
  readonly doc: Y.Doc;
  readonly awareness: awarenessProtocol.Awareness;
  readonly matterId: string;
  readonly documentId: string;
  readonly user: YjsUser;

  private _connected = false;
  private _destroyed = false;
  private _synced = false;
  private _unsubDocOp: DestroyCallback | null = null;
  private _unsubPresence: DestroyCallback | null = null;
  private _unsubRoster: DestroyCallback | null = null;
  private _unsubConnection: DestroyCallback | null = null;
  private _heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private _awarenessResendInterval: ReturnType<typeof setInterval> | null = null;
  private _syncTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Counters for logging (avoid spamming every message)
  private _stats = { sent: 0, received: 0, applied: 0, filtered: 0 };

  // Track active peers for presence
  private _peers: Map<string, { name: string; color: string; email?: string; profilePicture?: string }> = new Map();
  private _onPeersChange: ((peers: Map<string, { name: string; color: string; email?: string; profilePicture?: string }>) => void) | null = null;

  // Connection status callback
  private _onConnectionChange: ((connected: boolean) => void) | null = null;
  // Synced status callback
  private _onSyncedChange: ((synced: boolean) => void) | null = null;

  get connected(): boolean { return this._connected; }
  get synced(): boolean { return this._synced; }
  get destroyed(): boolean { return this._destroyed; }

  constructor(opts: YjsWebPubSubProviderOptions) {
    this.doc = opts.doc;
    this.matterId = opts.matterId;
    this.documentId = opts.documentId;
    this.user = opts.user;
    this.awareness = opts.awareness || new awarenessProtocol.Awareness(this.doc);

    // Set local awareness state (user info for cursor labels).
    // IMPORTANT: Use setLocalState (not setLocalStateField) because after a
    // React StrictMode double-mount, the previous provider.destroy() calls
    // removeAwarenessStates which deletes the state from the map. After that,
    // getLocalState() returns null, and setLocalStateField silently no-ops.
    const currentState = this.awareness.getLocalState() || {};
    this.awareness.setLocalState({
      ...currentState,
      user: {
        id: this.user.id,
        name: this.user.name,
        color: this.user.color,
        email: this.user.email || '',
        profilePicture: this.user.profilePicture || '',
      },
    });

    console.log(`${LOG_PREFIX} Created for doc=${this.documentId} matter=${this.matterId} user=${this.user.id} (${this.user.name}) clientID=${this.doc.clientID} awarenessState=${JSON.stringify(this.awareness.getLocalState())}`);
  }

  /**
   * Connect to the collaboration hub and start syncing.
   */
  async connect(): Promise<void> {
    if (this._destroyed) return;
    const collab = getCollaborationService(this.user.id);

    console.log(`${LOG_PREFIX} Connecting...`);

    // Initialize and join
    const connected = await collab.initialize(this.user.id);
    if (!connected || this._destroyed) {
      console.warn(`${LOG_PREFIX} ✗ Failed to connect to collaboration hub`);
      return;
    }

    console.log(`${LOG_PREFIX} Hub connected, joining matter group...`);
    collab.joinMatter(this.matterId, this.user.id);

    // ── Subscribe to events BEFORE marking connected ────────────────
    // This ensures we don't miss any events that arrive right after joining

    // ── Subscribe to doc_op events ──────────────────────────────────
    this._unsubDocOp = collab.onDocOp((data) => {
      if (this._destroyed) return;

      // Skip own messages (server already excludes sender via OData filter,
      // but double-check in case of echo or race)
      if (String(data.userId) === String(this.user.id)) {
        this._stats.filtered++;
        return;
      }

      // Only handle messages for this document
      const opsDocId = data.ops?.documentId;
      if (opsDocId && opsDocId !== this.documentId) {
        this._stats.filtered++;
        return;
      }

      const ops = data.ops;
      if (!ops) {
        console.warn(`${LOG_PREFIX} ⚠ Received doc_op with no ops payload from user=${data.userId}`);
        return;
      }

      // Handle Yjs CRDT sync messages
      if (ops.yjsType && ops.yjsData) {
        this._stats.received++;
        try {
          const binaryData = Uint8Array.from(atob(ops.yjsData), c => c.charCodeAt(0));

          switch (ops.yjsType) {
            case 'sync-step-1': {
              console.log(`${LOG_PREFIX} ◀ RECV sync-step-1 from user=${data.userId} (${binaryData.length}B)`);
              const sv = binaryData;
              const update = Y.encodeStateAsUpdate(this.doc, sv);
              this._sendYjsMessage('sync-step-2', update);
              // Also send our full awareness so the new peer knows about us
              this._broadcastFullAwareness();
              break;
            }
            case 'sync-step-2': {
              console.log(`${LOG_PREFIX} ◀ RECV sync-step-2 from user=${data.userId} (${binaryData.length}B)`);
              Y.applyUpdate(this.doc, binaryData, this);
              this._stats.applied++;
              if (!this._synced) {
                this._synced = true;
                this._onSyncedChange?.(true);
                console.log(`${LOG_PREFIX} ✓ Synced with peer`);
              }
              break;
            }
            case 'update': {
              Y.applyUpdate(this.doc, binaryData, this);
              this._stats.applied++;
              // Log every 10th update to avoid spam
              if (this._stats.applied % 10 === 0) {
                console.log(`${LOG_PREFIX} ◀ Applied ${this._stats.applied} remote updates total (latest from user=${data.userId}, ${binaryData.length}B)`);
              }
              break;
            }
            case 'awareness': {
              awarenessProtocol.applyAwarenessUpdate(
                this.awareness,
                binaryData,
                this
              );
              break;
            }
            default:
              console.warn(`${LOG_PREFIX} ⚠ Unknown yjsType: ${ops.yjsType}`);
          }
        } catch (err) {
          console.error(`${LOG_PREFIX} ✗ Error processing ${ops.yjsType} message:`, err);
        }
        return;
      }

      // Non-Yjs doc_op (legacy OT operations) - log but ignore
      console.debug(`${LOG_PREFIX} Ignoring non-Yjs doc_op from user=${data.userId} type=${ops.type}`);
    });

    // ── Subscribe to presence events ────────────────────────────────
    this._unsubPresence = collab.onPresence((data) => {
      if (this._destroyed) return;
      if (String(data.userId) === String(this.user.id)) return;

      if (data.status === 'active') {
        console.log(`${LOG_PREFIX} 👤 Peer active: user=${data.userId}`);
        // New peer joined - send sync step 1 + awareness
        this._sendSyncStep1();
        this._broadcastFullAwareness();
      } else if (data.status === 'inactive') {
        console.log(`${LOG_PREFIX} 👤 Peer inactive: user=${data.userId}`);
        this._peers.delete(data.userId);
        this._onPeersChange?.(new Map(this._peers));
      }
    });

    // ── Subscribe to roster events ──────────────────────────────────
    this._unsubRoster = collab.onRoster((data) => {
      if (this._destroyed) return;
      if (data.roster) {
        const activePeers = Object.entries(data.roster)
          .filter(([uid, status]) => status === 'active' && uid !== this.user.id);
        console.log(`${LOG_PREFIX} 📋 Roster received: ${activePeers.length} active peer(s)`, activePeers.map(([uid]) => uid));
        if (activePeers.length > 0) {
          this._sendSyncStep1();
          this._broadcastFullAwareness();
        }
      }
    });

    // ── Subscribe to connection status changes ──────────────────────
    this._unsubConnection = collab.onConnectionStatusChange((isConnected) => {
      if (this._destroyed) return;
      const wasConnected = this._connected;
      this._connected = isConnected;
      this._onConnectionChange?.(isConnected);

      if (isConnected && !wasConnected) {
        console.log(`${LOG_PREFIX} 🔄 Reconnected - rejoining group`);
        collab.rejoinMatter(this.matterId, this.user.id);
        // Delay to let group membership propagate
        setTimeout(() => {
          if (!this._destroyed) {
            this._sendSyncStep1();
            this._broadcastFullAwareness();
          }
        }, POST_JOIN_DELAY);
      } else if (!isConnected && wasConnected) {
        console.warn(`${LOG_PREFIX} ⚠ Disconnected`);
      }
    });

    // ── Listen for local Y.Doc updates ──────────────────────────────
    this.doc.on('update', this._onDocUpdate);

    // ── Listen for local awareness changes ──────────────────────────
    this.awareness.on('update', this._onAwarenessUpdate);

    // Now mark as connected
    this._connected = true;
    this._onConnectionChange?.(true);

    // ── Small delay after joining before sending initial messages ────
    // WebPubSub needs a moment to propagate group membership.
    // Without this, our sync-step-1 might be broadcast before the
    // server has added our connection to the group.
    await new Promise(resolve => setTimeout(resolve, POST_JOIN_DELAY));
    if (this._destroyed) return;

    // ── Initial sync: send step 1 + awareness ───────────────────────
    console.log(`${LOG_PREFIX} ▶ Sending initial sync-step-1 and awareness`);
    this._sendSyncStep1();
    this._broadcastFullAwareness();

    // Set a sync timeout - if we don't get step 2 within 3s, consider synced
    // (we might be the only peer)
    this._syncTimeoutId = setTimeout(() => {
      if (!this._synced && !this._destroyed) {
        console.log(`${LOG_PREFIX} ⏱ Sync timeout - no peers responded, marking synced`);
        this._synced = true;
        this._onSyncedChange?.(true);
      }
    }, 3000);

    // ── Heartbeat: send presence ping every 30s ─────────────────────
    this._heartbeatInterval = setInterval(() => {
      if (!this._destroyed && this._connected) {
        collab.sendPresencePing(this.matterId);
        // Log stats periodically
        console.log(`${LOG_PREFIX} 📊 Stats: sent=${this._stats.sent} recv=${this._stats.received} applied=${this._stats.applied} filtered=${this._stats.filtered}`);
      }
    }, 30_000);

    // ── Periodic awareness resend ───────────────────────────────────
    // Re-broadcast our full awareness state periodically so late joiners
    // and peers who missed the first message eventually get our state.
    this._awarenessResendInterval = setInterval(() => {
      if (!this._destroyed && this._connected) {
        this._broadcastFullAwareness();
      }
    }, AWARENESS_RESEND_INTERVAL);

    console.log(`${LOG_PREFIX} ✓ Fully connected and syncing`);
  }

  /**
   * Handle local Y.Doc update - broadcast to peers.
   */
  private _onDocUpdate = (update: Uint8Array, origin: any) => {
    // Skip if update came from this provider (remote), initial-load, or destroyed
    if (origin === this || origin === 'initial-load' || this._destroyed) return;

    this._sendYjsMessage('update', update);
  };

  /**
   * Handle local awareness update - broadcast to peers.
   */
  private _onAwarenessUpdate = ({ added, updated, removed }: any, origin: any) => {
    // Skip awareness updates that came from applying remote state
    if (origin === this || this._destroyed) return;

    const changedClients = [...(added || []), ...(updated || []), ...(removed || [])];
    if (changedClients.length === 0) return;

    const encodedAwareness = awarenessProtocol.encodeAwarenessUpdate(
      this.awareness,
      changedClients
    );
    this._sendYjsMessage('awareness', encodedAwareness);
  };

  /**
   * Broadcast our full current awareness state (user info + cursor) to all peers.
   * Called after connecting, on reconnection, when a new peer joins, and periodically.
   */
  private _broadcastFullAwareness() {
    if (this._destroyed || !this._connected) return;

    const localState = this.awareness.getLocalState();
    console.log(`${LOG_PREFIX} Broadcasting awareness: user=${localState?.user?.name || 'NONE'} color=${localState?.user?.color || 'NONE'} cursor=${!!localState?.cursor}`);

    const encodedAwareness = awarenessProtocol.encodeAwarenessUpdate(
      this.awareness,
      [this.doc.clientID]
    );
    this._sendYjsMessage('awareness', encodedAwareness);
  }

  /**
   * Send a Yjs binary message over PubSub as base64.
   */
  private _sendYjsMessage(yjsType: string, data: Uint8Array) {
    if (this._destroyed || !this._connected) {
      return;
    }

    this._stats.sent++;

    // Use Array.from for large Uint8Arrays to avoid spread operator stack overflow
    let base64Data: string;
    if (data.length > 10_000) {
      let binary = '';
      for (let i = 0; i < data.length; i++) {
        binary += String.fromCharCode(data[i]);
      }
      base64Data = btoa(binary);
    } else {
      base64Data = btoa(String.fromCharCode(...data));
    }

    const collab = getCollaborationService();
    collab.sendDocumentOp(this.matterId, {
      type: 'yjs',
      position: 0,
      userId: this.user.id,
      userUID: this.user.id,
      documentId: this.documentId,
      yjsType,
      yjsData: base64Data,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Send sync step 1 (our state vector) to request missing updates from peers.
   */
  private _sendSyncStep1() {
    if (this._destroyed) return;
    const sv = Y.encodeStateVector(this.doc);
    this._sendYjsMessage('sync-step-1', sv);
  }

  /**
   * Set callback for peer changes (presence tracking).
   */
  onPeersChange(cb: (peers: Map<string, { name: string; color: string; email?: string; profilePicture?: string }>) => void) {
    this._onPeersChange = cb;
  }

  /**
   * Set callback for connection status changes.
   */
  onConnectionChange(cb: (connected: boolean) => void) {
    this._onConnectionChange = cb;
  }

  /**
   * Set callback for synced status changes.
   */
  onSyncedChange(cb: (synced: boolean) => void) {
    this._onSyncedChange = cb;
  }

  /**
   * Destroy the provider - cleanup all subscriptions and intervals.
   */
  destroy() {
    if (this._destroyed) return;
    console.log(`${LOG_PREFIX} Destroying provider (stats: sent=${this._stats.sent} recv=${this._stats.received} applied=${this._stats.applied})`);
    this._destroyed = true;
    this._connected = false;

    // Clear timers
    if (this._heartbeatInterval) clearInterval(this._heartbeatInterval);
    if (this._awarenessResendInterval) clearInterval(this._awarenessResendInterval);
    if (this._syncTimeoutId) clearTimeout(this._syncTimeoutId);

    // Remove Y.Doc and awareness listeners
    this.doc.off('update', this._onDocUpdate);
    this.awareness.off('update', this._onAwarenessUpdate);

    // Unsubscribe from PubSub events
    this._unsubDocOp?.();
    this._unsubPresence?.();
    this._unsubRoster?.();
    this._unsubConnection?.();

    // Leave the matter
    try {
      const collab = getCollaborationService();
      collab.leaveMatter(this.matterId);
    } catch (err) {
      console.debug(`${LOG_PREFIX} Error leaving matter on destroy:`, err);
    }

    // NOTE: Do NOT call removeAwarenessStates here.
    // The awareness instance is shared with the editor component (created in
    // useMemo) and outlives the provider. In React StrictMode, destroy() runs
    // on the first mount's cleanup, then a new provider is created - if we
    // deleted the awareness state here, the new provider's setLocalStateField
    // would silently no-op (it checks state !== null). The awareness will be
    // cleaned up when the editor component fully unmounts and the Awareness
    // instance is garbage collected.
  }
}

/**
 * Get the deterministic client ID for initial content loading.
 * All peers use this same ID so Yjs de-duplicates the initial HTML insert.
 */
export function getInitialContentClientID(): number {
  return INITIAL_CONTENT_CLIENT_ID;
}

export default YjsWebPubSubProvider;
